/**
 * Notification data layer — TanStack Query hooks over the gated in-app
 * notifications API (`/api/teams/{teamId}/tasks/notifications`). This is the
 * ONLY place the notifications layer talks to the server; the rendering
 * component (the unified attention bell) consumes these hooks and never calls
 * `apiFetch` directly — the same query ⟂ view split the rest of taskmap keeps
 * (§A7), mirroring `useReminders` / `useNodeNotes`.
 *
 * Issue #907 (note-driven notifications). A notification is the CALLER's OWN
 * record that a note happened on a task they care about: either they were
 * @mentioned, or they subscribed to that task's notes. It is **strictly the
 * caller's own slice** — the backend resolves it against the requesting
 * membership with NO `member_id` parameter, so a client cannot request anyone
 * else's feed. NEVER framed as an activity feed about other people, NEVER a
 * per-person tally / ranking (§A9 brand firewall).
 *
 * Two read shapes off ONE query keyed `["task-notifications", teamId]`:
 *   - the **unread badge count** — a cheap `unread_only=true&limit=1` fetch
 *     that runs whenever the bell could surface a badge, so the toolbar can
 *     show "•N" without pulling the whole list;
 *   - the **full feed** — the same key, refetched with the un-capped list when
 *     the bell is actually open.
 * Both mutations (mark-one-read + mark-all-read) invalidate the key so the
 * badge and the open list re-derive from one fresh source of truth.
 *
 * Gating: these hooks are only *consumed* behind `task_notes` (off by default,
 * §A10); the backend enforces the gate too (404-dark when off). The query is
 * `enabled` only when the surface that needs it is showing (the bell can badge
 * OR is open) and degrades calmly on a 404 (`retry: false`, empty list + zero
 * count) so an undeployed / ungated endpoint never crashes the toolbar.
 *
 * Isolation: imports only the generic `apiFetch` + the generated schema
 * types — no scheduler coupling, no rendering.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "../../api/client";
import type { components } from "../../api/schema.gen";

/** One notification, as it arrives from the API. */
export type TaskNotification = components["schemas"]["TaskNotificationRead"];
type NotificationListResponse = components["schemas"]["NotificationListResponse"];

/**
 * Cache PREFIX for the caller's notification slice on a team. Exported for
 * tests + invalidation: invalidating this prefix matches BOTH the badge fetch
 * and the open-bell full fetch (TanStack matches by key prefix), so marking
 * read refreshes the badge AND the open list from one call.
 */
export const TASK_NOTIFICATIONS_KEY = (teamId: string): readonly string[] => [
  "task-notifications",
  teamId,
];

/**
 * The full cache key for one read shape. The badge fetch (`full: false`,
 * unread-only + capped) and the open-bell fetch (`full: true`, the whole list)
 * are SEPARATE cache entries — a cheap capped fetch must never clobber the full
 * list under the same key — but both live under the shared prefix above, so one
 * prefix-invalidation refreshes both.
 */
const notificationsKey = (teamId: string, full: boolean): readonly (string | boolean)[] => [
  "task-notifications",
  teamId,
  full ? "full" : "badge",
];

const STALE_TIME_MS = 30_000;

function notificationsUrl(teamId: string, opts?: { unreadOnly?: boolean; limit?: number }): string {
  const params = new URLSearchParams();
  if (opts?.unreadOnly) params.set("unread_only", "true");
  if (opts?.limit != null) params.set("limit", String(opts.limit));
  const qs = params.toString();
  return `/api/teams/${teamId}/tasks/notifications${qs ? `?${qs}` : ""}`;
}

export interface UseNotificationsResult {
  /** The caller's own notifications, newest-first (as the endpoint returns). */
  readonly items: TaskNotification[];
  /** The caller's TOTAL unread count (the badge number) — independent of `limit`. */
  readonly unreadCount: number;
  readonly isLoading: boolean;
  /** True on any fetch failure (incl. a not-yet-deployed / ungated 404). */
  readonly isError: boolean;
}

function toResult(query: {
  data?: NotificationListResponse | undefined;
  isLoading: boolean;
  isError: boolean;
}): UseNotificationsResult {
  const items = Array.isArray(query.data?.items) ? query.data.items : [];
  const unreadCount = typeof query.data?.unread_count === "number" ? query.data.unread_count : 0;
  return { items, unreadCount, isLoading: query.isLoading, isError: query.isError };
}

export interface UseNotificationsOptions {
  /**
   * Pull the FULL feed (the bell is open). When false we keep the request
   * cheap — only `unread_count` matters for the badge, so we cap the list to
   * one row. The cache key is the same either way, so opening the bell simply
   * widens what the one query holds.
   */
  readonly full?: boolean;
}

/**
 * Fetch the caller's notification slice for the bell. `enabled` only when both
 * the team id is present AND `active` (the bell could badge OR is open), so no
 * request fires for a surface that isn't showing. With `full` it pulls the
 * un-capped list (the open bell); without it, a cheap badge-only fetch
 * (`unread_only`, `limit=1`) that still returns the total `unread_count`.
 * Degrades to an empty list + zero count on a 404.
 */
export function useNotifications(
  teamId: string | undefined,
  active: boolean,
  options: UseNotificationsOptions = {},
): UseNotificationsResult {
  const full = options.full === true;
  const query = useQuery({
    queryKey: notificationsKey(teamId ?? "", full),
    enabled: Boolean(teamId) && active,
    staleTime: STALE_TIME_MS,
    // No retry: a not-yet-deployed / ungated endpoint should fail fast to the
    // calm "unavailable" state rather than spinning.
    retry: false,
    queryFn: () =>
      apiFetch<NotificationListResponse>(
        notificationsUrl(teamId as string, full ? {} : { unreadOnly: true, limit: 1 }),
      ),
  });
  return toResult(query);
}

/**
 * Mark ONE notification read (POST .../read → 204). Invalidates the caller's
 * slice so the badge count drops and the open list re-renders that row as read
 * on success. The backend is idempotent + scopes the mark to the recipient
 * (403 for another member's notification, 404 if it doesn't exist); the UI only
 * ever marks the caller's own rows, so neither is normally reachable.
 */
export function useMarkNotificationRead(teamId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: string) =>
      apiFetch<void>(`/api/teams/${teamId}/tasks/notifications/${notificationId}/read`, {
        method: "POST",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: TASK_NOTIFICATIONS_KEY(teamId ?? "") }),
  });
}

/**
 * Mark ALL of the caller's notifications read (POST .../read-all → 204).
 * Invalidates the caller's slice so the badge clears + the open list shows
 * every row read. Idempotent server-side (a no-op when nothing was unread);
 * scoped to the caller's own slice — it can never touch another member's feed.
 */
export function useMarkAllNotificationsRead(teamId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<void>(`/api/teams/${teamId}/tasks/notifications/read-all`, {
        method: "POST",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: TASK_NOTIFICATIONS_KEY(teamId ?? "") }),
  });
}
